import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { getAirportDisplayTextDropdown } from 'lib/flights/flightUtils'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'

interface Props {
  subRecord?: boolean
  selected?: boolean
  airport: App.AirportLocation
  icon?: React.ReactNode
  onSelectAirport: (airport: App.AirportLocation) => void
  id?: string
}

const StyledClickableListItem = styled(ClickableListItem)`
  &.sub-record {
    padding-inline-start: ${rem(50)};
  }
`

function isAllAirportsOption(airport: App.AirportLocation): boolean {
  return airport.airportCode.endsWith('ALL') && airport.isMultiAirport !== false
}

function AirportDropdownOption(props: Props) {
  const intl = useIntl()
  const { id, subRecord, selected, airport, icon, onSelectAirport } = props
  const { cityDesc, country } = airport
  const selectAirport = () => onSelectAirport(airport)

  const isAllAirports = useMemo(() => isAllAirportsOption(airport), [airport])

  const airportIcon = useMemo(() => {
    if (isAllAirports) {
      return <LineMapMarkerIcon colour="primary" />
    }

    if (subRecord) {
      return <LinePlaneDepartureIcon colour="primary" />
    }
    return icon ?? <LinePlaneDepartureIcon colour="primary" />
  }, [isAllAirports, subRecord, icon])

  const airportSubtitle = useMemo(() => {
    if (isAllAirports) return country
    if (!cityDesc) return country
    if (!country) return cityDesc

    return intl.formatMessage(
      {
        id: 'xi0qllrLuYB47vW2Y-Gb',
        defaultMessage: '{cityDesc}, {country}',
        description: 'Airport dropdown option city and country separated by comma',
      },
      { cityDesc, country },
    )
  }, [cityDesc, country, intl, isAllAirports])

  return (
    <StyledClickableListItem
      size="medium"
      onClick={selectAirport}
      startIcon={airportIcon}
      className={clsx({ 'sub-record': subRecord && !isAllAirports })}
      title={getAirportDisplayTextDropdown(airport)}
      subtitle={airportSubtitle}
      data-testid={`${id}-${airport.airportCode}`}
      selected={selected}
    />
  )
}

export default AirportDropdownOption
